import { supabase } from '../lib/supabase.js';

/**
 * Date utility functions for safe date operations
 */
const DateUtils = {
  /**
   * Safely create a Date object with validation
   * Handles date-only strings properly to avoid timezone issues
   */
  safeCreateDate(input) {
    if (!input) return null;

    // If input is a date-only string (YYYY-MM-DD), create date in local timezone
    // to avoid timezone shift when converting to/from UTC
    if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
      const [year, month, day] = input.trim().split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(input); 
    return isNaN(date.getTime()) ? null : date;
  },

  /**
   * Safely convert a Date to ISO string with validation
   * Uses local date components to avoid timezone conversion issues
   */
  safeDateToISO(date) {
    if (!date || isNaN(date.getTime())) return null;

    // Use local date components instead of UTC to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  },

  /**
   * Create a safe fallback date for graduation year calculation
   */
  createGraduationFallback(graduationYear) {
    // If no graduation year, use current year + 1 as default
    const year = graduationYear || (new Date().getFullYear() + 1);
    
    // Validate year is reasonable (between 1900 and 2100)
    if (year < 1900 || year > 2100) {
      console.warn(`Invalid graduation year: ${year}, using current year + 1`);
      return new Date().getFullYear() + 1;
    }
    
    return year;
  }
};

/**
 * Core Anchor Registry Service
 * Manages anchor point calculations for milestone due dates
 */

// Essential anchor definitions (10 core anchors)
const ESSENTIAL_ANCHORS = {
  // Student lifecycle anchors
  'timeline_baseline': {
    id: 'timeline_baseline',
    name: 'Student Planning Start Date',
    description: 'The effective planning start date based on student timeline position',
    calculation: (student) => {
      const actualStartDate = new Date(student.timeline_baseline_date || student.created_at || new Date());

      // Use same graduation year logic as TimelineInterpolationService
      let graduationYear;
      if (student.graduation_year) {
        graduationYear = student.graduation_year;
      } else {
        const startYear = actualStartDate.getFullYear();
        if (startYear === 2025 || startYear === 2024) {
          graduationYear = 2026; // 2025-2026 academic cycle
        } else if (startYear >= 2026) {
          graduationYear = startYear + 1;
        } else {
          graduationYear = startYear + 2; // Conservative assumption
        }
      }

      const juniorYear = graduationYear - 2;
      const seniorYear = graduationYear - 1;

      const maxTimelineStart = new Date(juniorYear, 4, 10); // May 10 junior year
      const minTimelineStart = new Date(seniorYear, 8, 10); // Sep 10 senior year

      // If student started on/before May 10, use May 10 as anchor
      if (actualStartDate <= maxTimelineStart) {
        console.log(`[timeline_baseline] Student started early (${actualStartDate.toDateString()}), using May 10 anchor: ${maxTimelineStart.toDateString()}`);
        return maxTimelineStart;
      }

      // If student started on/after Sep 10, use Sep 10 as anchor
      if (actualStartDate >= minTimelineStart) {
        console.log(`[timeline_baseline] Student started late (${actualStartDate.toDateString()}), using Sep 10 anchor: ${minTimelineStart.toDateString()}`);
        return minTimelineStart;
      }

      // Student started between bounds - use their actual date
      console.log(`[timeline_baseline] Student started mid-timeline: ${actualStartDate.toDateString()}`);
      return actualStartDate;
    },
    fallback: () => new Date(),
    validation: (date) => date <= new Date(),
    student_configurable: true
  },

  'senior_year_start': {
    id: 'senior_year_start',
    name: 'Senior Year Start',
    description: 'Beginning of student\'s senior year (September 1)',
    calculation: (student) => {
      const seniorYear = student.graduation_year - 1;
      return new Date(seniorYear, 8, 1); // September 1
    },
    fallback: () => new Date(new Date().getFullYear(), 8, 1),
    validation: (date) => date.getMonth() === 8 && date.getDate() === 1
  },

  'graduation': {
    id: 'graduation',
    name: 'Graduation',
    description: 'Student\'s graduation date',
    calculation: (student) => {
      const graduationYear = student.graduation_year;
      return new Date(graduationYear, 5, 1); // June 1
    },
    fallback: () => new Date(new Date().getFullYear(), 5, 1),
    validation: (date) => date.getMonth() === 5 && date.getDate() === 1
  },

  // Federal deadline anchors
  'fafsa_opens': {
    id: 'fafsa_opens',
    name: 'FAFSA Opens',
    description: 'Federal FAFSA application opens (October 1)',
    calculation: (student) => {
      const seniorYear = student.graduation_year;
      return new Date(seniorYear - 1, 9, 1); // October 1 of senior year
    },
    fallback: () => new Date(new Date().getFullYear(), 9, 1),
    validation: (date) => date.getMonth() === 9 && date.getDate() === 1
  },

  // College deadline anchors
  'ed_deadline': {
    id: 'ed_deadline',
    name: 'Early Decision Deadline',
    description: 'College-specific Early Decision deadline',
    calculation: (student, college) => {
      // Handle three scenarios:
      // 1. No college set for ED (returns null/undefined)
      // 2. College set but no ED deadline (returns null/undefined) 
      // 3. College set with ED deadline (may be string from DB)
      
      if (!college?.global_colleges) {
        console.log(`No college data available for ED deadline calculation`);
        return null;
      }
      
      const edDeadline = college.global_colleges.ed_deadline;
      
      if (!edDeadline) {
        console.log(`No ED deadline found for college: ${college.name || 'Unknown'}`);
        return null;
      }
      
      // Convert string dates from database to Date objects
      const deadlineDate = DateUtils.safeCreateDate(edDeadline);
      if (!deadlineDate) {
        console.warn(`Invalid ED deadline format for college ${college.name}: ${edDeadline}`);
        return null;
      }
      
      console.log(`Found ED deadline for ${college.name}: ${DateUtils.safeDateToISO(deadlineDate)}`);
      return deadlineDate;
    },
    fallback: (student) => {
      console.log(`Using ED deadline fallback for student graduation year: ${student.graduation_year}`);
      const safeGradYear = DateUtils.createGraduationFallback(student.graduation_year);
      // November 1 of senior year minus 1 (so November 1 of junior year for applications)
      return new Date(safeGradYear - 1, 10, 1); // November 1
    },
    validation: (date) => date > new Date(),
    requires_college: true
  },

  'ea_deadline': {
    id: 'ea_deadline',
    name: 'Early Action Deadline',
    description: 'College-specific Early Action deadline',
    calculation: (student, college) => {
      if (!college?.global_colleges?.ea_deadline) return null;
      const deadlineDate = DateUtils.safeCreateDate(college.global_colleges.ea_deadline);
      if (!deadlineDate) {
        console.warn(`Invalid EA deadline format for college ${college.name}: ${college.global_colleges.ea_deadline}`);
        return null;
      }
      return deadlineDate;
    },
    fallback: (student) => {
      const safeGradYear = DateUtils.createGraduationFallback(student.graduation_year);
      return new Date(safeGradYear - 1, 10, 1); // November 1
    },
    validation: (date) => date > new Date(),
    requires_college: true
  },

  'rd_deadline': {
    id: 'rd_deadline',
    name: 'Regular Decision Deadline',
    description: 'College-specific Regular Decision deadline',
    calculation: (student, college) => {
      if (!college?.global_colleges?.rd_deadline) return null;
      const deadlineDate = DateUtils.safeCreateDate(college.global_colleges.rd_deadline);
      if (!deadlineDate) {
        console.warn(`Invalid RD deadline format for college ${college.name}: ${college.global_colleges.rd_deadline}`);
        return null;
      }
      return deadlineDate;
    },
    fallback: (student) => {
      console.log(`🔍 [AnchorRegistry] RD deadline fallback - student graduation_year:`, student.graduation_year);
      const safeGradYear = DateUtils.createGraduationFallback(student.graduation_year);
      const rdDate = new Date(safeGradYear, 0, 1); // January 1 of graduation year (mid-senior year)
      console.log(`🔍 [AnchorRegistry] RD deadline fallback - calculated date:`, rdDate.toDateString());
      return rdDate;
    },
    validation: (date) => date > new Date(),
    requires_college: true
  },

  'finaid_deadline': {
    id: 'finaid_deadline',
    name: 'Financial Aid Deadline',
    description: 'College-specific financial aid deadline',
    calculation: (student, college) => college.global_colleges?.finaid_deadline,
    fallback: (student) => {
      const seniorYear = student.graduation_year;
      return new Date(seniorYear, 1, 1); // February 1
    },
    validation: (date) => date > new Date(),
    requires_college: true
  },

  'css_deadline': {
    id: 'css_deadline',
    name: 'CSS Profile Deadline',
    description: 'College-specific CSS Profile deadline',
    calculation: (student, college) => college.global_colleges?.css_deadline,
    fallback: (student) => {
      const seniorYear = student.graduation_year;
      return new Date(seniorYear, 1, 1); // February 1
    },
    validation: (date) => date > new Date(),
    requires_college: true
  },

  // Calculated anchors
  'national_decision_day': {
    id: 'national_decision_day',
    name: 'National Decision Day',
    description: 'National deadline to commit to college (May 1)',
    calculation: (student, college) => {
      // Ensure we have a valid graduation year
      const graduationYear = student.graduation_year || (new Date().getFullYear() + 1);

      console.log(`[AnchorRegistry] national_decision_day calculation:`, {
        student_id: student.id,
        graduation_year: student.graduation_year,
        computed_graduation_year: graduationYear,
        result_date: new Date(graduationYear, 4, 1).toISOString().split('T')[0]
      });

      // Validate the year is reasonable (not too far in future)
      if (graduationYear > new Date().getFullYear() + 4) {
        console.warn(`[AnchorRegistry] Suspicious graduation year ${graduationYear}, using default`);
        const defaultYear = new Date().getFullYear() + 1;
        return new Date(defaultYear, 4, 1); // May 1
      }

      return new Date(graduationYear, 4, 1); // May 1 of graduation year
    },
    fallback: (student) => {
      const graduationYear = student.graduation_year || (new Date().getFullYear() + 1);
      return new Date(graduationYear, 4, 1); // May 1
    },
    validation: (date) => date > new Date(),
    requires_college: false // Changed to false since we don't actually use college data
  },

  // Alias for Timeline Builder compatibility
  'decision_day': {
    id: 'decision_day',
    name: 'Decision Day (alias for national_decision_day)',
    description: 'Alias for national_decision_day for Timeline Builder compatibility',
    calculation: (student, college) => {
      // Delegate to national_decision_day
      return ESSENTIAL_ANCHORS['national_decision_day'].calculation(student, college);
    },
    fallback: (student) => {
      return ESSENTIAL_ANCHORS['national_decision_day'].fallback(student);
    },
    validation: (date) => date > new Date(),
    requires_college: false
  },
 

  'task_completion': {
    id: 'task_completion',
    name: 'Dependent Task Completion',
    description: 'When a prerequisite task is completed',
    calculation: async (student, college, templateId) => {
      const { data: task } = await supabase
        .from('timeline_items')
        .select('completion_date')
        .eq('user_id', student.id)
        .eq('template_id', templateId)
        .eq('is_completed', true)
        .single();
      
      return task?.completion_date || null;
    },
    fallback: () => new Date(),
    validation: (date) => date <= new Date(),
    requires_template_id: true,
    async: true
  },

  // College-agnostic deadline anchor
  'earliest_application_deadline': {
    id: 'earliest_application_deadline',
    name: 'Earliest Application Deadline',
    description: 'Student\'s earliest deadline across all colleges (college-agnostic)',
    calculation: (student) => {
      if (!student.earliest_application_deadline) {
        console.warn(`No earliest_application_deadline found for student ${student.id}`);
        return null;
      }
      
      const deadlineDate = DateUtils.safeCreateDate(student.earliest_application_deadline);
      if (!deadlineDate) {
        console.warn(`Invalid earliest deadline format for student ${student.id}: ${student.earliest_application_deadline}`);
        return null;
      }
      
      console.log(`Using earliest application deadline: ${deadlineDate.toDateString()}`);
      return deadlineDate;
    },
    fallback: (student) => {
      // Default to Oct 29 if no earliest deadline available
      const safeGradYear = DateUtils.createGraduationFallback(student.graduation_year);
      return new Date(safeGradYear - 1, 9, 29); // October 29
    },
    validation: (date) => date > new Date(),
    requires_college: false // This is college-agnostic
  },

  // Smart dependency anchor for post-application tasks
  'all_applications_completed': {
    id: 'all_applications_completed',
    name: 'All Applications Completed',
    description: 'When student completes ALL college application tasks across their entire college list',
    calculation: async (student) => {
      // Import here to avoid circular dependency
      const { supabase } = await import('../lib/supabase');
      
      console.log(`[AnchorRegistry] Checking all applications completion for student ${student.id}`);
      
      // Get all student's colleges
      const { data: colleges, error: collegesError } = await supabase
        .from('college_journey_colleges')
        .select('id, name')
        .eq('user_id', student.id);
      
      if (collegesError || !colleges?.length) {
        console.warn(`No colleges found for student ${student.id}`);
        return null;
      }
      
      // Define final application task categories/titles that must be completed
      const finalApplicationTasks = [
        'Submit Common Application to this college',
        'Submit final essays', 
        'Verify All Materials Received'
      ];
      
      let latestCompletionDate = null;
      let totalRequired = 0;
      let totalCompleted = 0;
      
      // Check completion status for each college
      for (const college of colleges) {
        for (const taskTitle of finalApplicationTasks) {
          totalRequired++;
          
          // Look for this task for this specific college
          const { data: task } = await supabase
            .from('timeline_items')
            .select('completion_date, is_completed')
            .eq('user_id', student.id)
            .eq('title', taskTitle)
            .eq('college_id', college.id)
            .single();
          
          if (task?.is_completed && task.completion_date) {
            totalCompleted++;
            const completionDate = new Date(task.completion_date);
            
            if (!latestCompletionDate || completionDate > latestCompletionDate) {
              latestCompletionDate = completionDate;
            }
          }
        }
      }
      
      console.log(`[AnchorRegistry] Application completion: ${totalCompleted}/${totalRequired} tasks completed`);
      
      // Only return date if ALL required tasks are completed
      if (totalCompleted === totalRequired && totalRequired > 0) {
        console.log(`[AnchorRegistry] All applications completed on: ${latestCompletionDate.toDateString()}`);
        return latestCompletionDate;
      }
      
      console.log(`[AnchorRegistry] Applications not yet completed (${totalCompleted}/${totalRequired})`);
      return null;
    },
    fallback: (student) => {
      // Safety net: Jan 1 of senior year even if apps aren't done
      const safeGradYear = DateUtils.createGraduationFallback(student.graduation_year);
      const safetyDate = new Date(safeGradYear - 1, 0, 1); // January 1
      console.log(`[AnchorRegistry] Using safety net date: ${safetyDate.toDateString()}`);
      return safetyDate;
    },
    validation: (date) => date <= new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // Within next year
    requires_college: false,
    async: true
  },

  // Context-aware college deadline anchor (replaces separate ed_deadline/rd_deadline)
  'college_application_deadline': {
    id: 'college_application_deadline',
    name: 'College Application Deadline',
    description: 'Smart context-aware anchor that uses the specific college\'s actual deadline (ED, EA, or RD)',
    calculation: (student, college) => {
      console.log(`\n🚨 [ANCHOR DATE DEBUG] 🚨`);
      console.log(`[AnchorRegistry] ===== COLLEGE APPLICATION DEADLINE CALCULATION =====`);
      console.log(`[AnchorRegistry] Student: ${student.first_name || 'Unknown'} ${student.last_name || 'Student'} (Class of ${student.graduation_year || 'Unknown'})`);
      console.log(`[AnchorRegistry] College: ${college?.name || 'Unknown'}`);
      
      if (!college?.global_colleges) {
        console.warn(`[AnchorRegistry] ❌ No college data available for college application deadline`);
        console.log(`[AnchorRegistry] College object:`, college);
        return null;
      }
      
      // Determine which deadline type this college uses based on application_round
      let deadlineDate = null;
      const round = college.application_round;
      
      console.log(`[AnchorRegistry] 🎯 APPLICATION ROUND ANALYSIS:`);
      console.log(`[AnchorRegistry]   Application round: ${round || 'null'}`);
      console.log(`[AnchorRegistry]   Available deadlines:`, {
        ed_deadline: college.global_colleges.ed_deadline,
        ea_deadline: college.global_colleges.ea_deadline,
        rd_deadline: college.global_colleges.rd_deadline
      });
      
      switch (round) {
        case 'early_decision':
        case 'early_decision_2':
          deadlineDate = DateUtils.safeCreateDate(college.global_colleges.ed_deadline);
          console.log(`[AnchorRegistry]   ✅ EARLY DECISION → Using ED deadline: ${college.global_colleges.ed_deadline}`);
          break;
          
        case 'early_action':
          deadlineDate = DateUtils.safeCreateDate(college.global_colleges.ea_deadline);
          console.log(`[AnchorRegistry]   ✅ EARLY ACTION → Using EA deadline: ${college.global_colleges.ea_deadline}`);
          break;
          
        case 'regular_decision':
        default:
          deadlineDate = DateUtils.safeCreateDate(college.global_colleges.rd_deadline);
          console.log(`[AnchorRegistry]   ✅ REGULAR DECISION → Using RD deadline: ${college.global_colleges.rd_deadline}`);
          break;
      }
      
      if (!deadlineDate) {
        console.warn(`[AnchorRegistry] ❌ Invalid deadline format for college ${college.name}: ${college.global_colleges[round + '_deadline']}`);
        return null;
      }
      
      console.log(`[AnchorRegistry] 🎯 FINAL ANCHOR DATE: ${deadlineDate.toDateString()} (${deadlineDate.toISOString().split('T')[0]})`);
      console.log(`[AnchorRegistry] ===== END ANCHOR CALCULATION =====\n`);
      return deadlineDate;
    },
    fallback: (student, college) => {
      // Fall back based on application round, or default to RD
      const safeGradYear = DateUtils.createGraduationFallback(student.graduation_year);
      const round = college?.application_round;
      
      console.log(`🔍 [AnchorRegistry] College deadline fallback - graduation year: ${safeGradYear}, round: ${round}`);
      
      if (round === 'early_decision' || round === 'early_action' || round === 'early_decision_2') {
        // ED/EA deadlines: November 1 of senior year (year before graduation)
        const edDate = new Date(safeGradYear - 1, 10, 1); // November 1, 2025 for Class of 2026
        console.log(`🔍 [AnchorRegistry] ED/EA deadline fallback: ${edDate.toDateString()}`);
        return edDate;
      } else {
        // RD deadlines: January 1 of graduation year (mid-senior year)
        const rdDate = new Date(safeGradYear, 0, 1); // January 1, 2026 for Class of 2026
        console.log(`🔍 [AnchorRegistry] RD deadline fallback: ${rdDate.toDateString()}`);
        return rdDate;
      }
    },
    validation: (date) => date > new Date(),
    requires_college: true
  }
};

/**
 * Anchor Registry Service
 */
class AnchorRegistryService {
  
  /**
   * Get all available anchors
   */
  static getAllAnchors() {
    return ESSENTIAL_ANCHORS;
  }

  /**
   * Get specific anchor definition
   */
  static getAnchor(anchorId) {
    return ESSENTIAL_ANCHORS[anchorId] || null;
  }

  /**
   * Validate anchor ID exists
   */
  static validateAnchorId(anchorId) {
    return this.getAnchor(anchorId) !== null;
  }

  /**
   * Get anchors that require college data
   */
  static getAnchorsRequiringCollege() {
    return Object.values(ESSENTIAL_ANCHORS)
      .filter(anchor => anchor.requires_college);
  }

  /**
   * Get student-configurable anchors
   */
  static getStudentConfigurableAnchors() {
    return Object.values(ESSENTIAL_ANCHORS)
      .filter(anchor => anchor.student_configurable);
  }

  /**
   * Calculate anchor date with template-level fallback support
   */
  static async calculateAnchorDateWithFallback(template, student, college = null) {
    const primaryAnchorId = template.anchor_id || 'timeline_baseline';
    
    console.log(`[AnchorRegistry] Trying primary anchor: ${primaryAnchorId}`);
    
    // Try primary anchor first
    const primaryDate = await this.calculateAnchorDate(primaryAnchorId, student, college, template.depends_on_template_id);
    
    // For anchors like 'all_applications_completed', null means conditions not met
    if (primaryDate && primaryAnchorId === 'all_applications_completed') {
      console.log(`[AnchorRegistry] Primary anchor succeeded: ${primaryDate.toDateString()}`);
      return primaryDate;
    }
    
    // If primary fails and fallback is configured, try fallback
    if ((!primaryDate || primaryDate === null) && template.fallback_anchor_id) {
      console.log(`[AnchorRegistry] Primary anchor failed, trying fallback: ${template.fallback_anchor_id}`);
      
      const fallbackDate = await this.calculateAnchorDate(template.fallback_anchor_id, student, college);
      
      if (fallbackDate && template.fallback_offset_days) {
        // Apply fallback offset
        const adjustedFallbackDate = new Date(fallbackDate);
        adjustedFallbackDate.setDate(adjustedFallbackDate.getDate() + template.fallback_offset_days);
        console.log(`[AnchorRegistry] Using fallback with offset: ${adjustedFallbackDate.toDateString()}`);
        return adjustedFallbackDate;
      }
      
      return fallbackDate;
    }
    
    // Return primary result (may be null)
    return primaryDate;
  }

  /**
   * Calculate anchor date with overrides and fallbacks
   */
  static async calculateAnchorDate(anchorId, student, college = null, templateId = null) {
    console.log(`[AnchorRegistry] calculateAnchorDate called with anchorId: "${anchorId}"`);
    console.log(`[AnchorRegistry] Available anchors:`, Object.keys(ESSENTIAL_ANCHORS));

    const anchor = this.getAnchor(anchorId);

    if (!anchor) {
      console.warn(`[AnchorRegistry] Unknown anchor type: "${anchorId}", available anchors:`, Object.keys(ESSENTIAL_ANCHORS));
      console.warn(`[AnchorRegistry] Falling back to timeline_baseline`);
      return this.calculateAnchorDate('timeline_baseline', student, college);
    }

    try {
      // Check for student override first
      const override = await this.getStudentOverride(student.id, anchorId);
      if (override) {
        console.log(`Using student override for ${anchorId}:`, override.override_date);
        return new Date(override.override_date);
      }

      // Validate requirements
      if (anchor.requires_college && !college) {
        console.warn(`Anchor ${anchorId} requires college data, using fallback`);
        return new Date(anchor.fallback(student, college));
      }

      if (anchor.requires_template_id && !templateId) {
        console.warn(`Anchor ${anchorId} requires template ID, using fallback`);
        return new Date(anchor.fallback(student, college));
      }

      // Calculate the anchor date
      let calculatedDate;
      
      if (anchor.async) {
        calculatedDate = await anchor.calculation(student, college, templateId);
      } else {
        calculatedDate = anchor.calculation(student, college, templateId);
      }

      // Use fallback if calculation failed
      if (!calculatedDate || calculatedDate === null) {
        console.log(`Anchor ${anchorId} calculation returned null, using fallback`);
        calculatedDate = anchor.fallback(student, college);
      }

      // Validate the calculated date
      const dateObj = new Date(calculatedDate);
      if (isNaN(dateObj.getTime())) {
        console.warn(`Anchor ${anchorId} returned invalid date, using fallback`);
        calculatedDate = anchor.fallback(student, college);
      }

      if (anchor.validation && !anchor.validation(dateObj)) {
        console.warn(`Anchor ${anchorId} validation failed, using fallback`);
        calculatedDate = anchor.fallback(student, college);
      }

      // Validate calculated date before using it
      const resultDate = new Date(calculatedDate);
      if (isNaN(resultDate.getTime())) {
        console.error(`Invalid calculated date for anchor ${anchorId}:`, calculatedDate);
        console.error(`Using fallback for anchor ${anchorId}`);
        const fallbackDate = new Date(anchor.fallback(student, college));
        if (isNaN(fallbackDate.getTime())) {
          // Ultimate fallback if primary fallback fails
          const ultimateFallback = new Date();
          ultimateFallback.setDate(ultimateFallback.getDate() + 30);
          console.error(`Primary fallback also invalid for ${anchorId}, using ultimate fallback: ${ultimateFallback.toISOString().split('T')[0]}`);
          return ultimateFallback;
        }
        console.log(`Fallback anchor ${anchorId}:`, DateUtils.safeDateToISO(fallbackDate));
        return fallbackDate;
      }
      
      const dateString = DateUtils.safeDateToISO(resultDate);
      console.log(`Calculated anchor ${anchorId}:`, dateString);
      return resultDate;

    } catch (error) {
      console.error(`Error calculating anchor ${anchorId}:`, error);
      try {
        const fallbackDate = new Date(anchor.fallback(student, college));
        if (isNaN(fallbackDate.getTime())) {
          // Ultimate fallback - 30 days from now
          const ultimateFallback = new Date();
          ultimateFallback.setDate(ultimateFallback.getDate() + 30);
          console.error(`Fallback also invalid for ${anchorId}, using ultimate fallback: ${ultimateFallback.toISOString().split('T')[0]}`);
          return ultimateFallback;
        }
        console.log(`Using fallback for ${anchorId}:`, DateUtils.safeDateToISO(fallbackDate));
        return fallbackDate;
      } catch (fallbackError) {
        console.error(`Fallback also failed for ${anchorId}:`, fallbackError);
        // Ultimate fallback - 30 days from now
        const ultimateFallback = new Date();
        ultimateFallback.setDate(ultimateFallback.getDate() + 30);
        console.error(`Using ultimate fallback for ${anchorId}: ${ultimateFallback.toISOString().split('T')[0]}`);
        return ultimateFallback;
      }
    }
  }

  /**
   * Get student override for specific anchor
   */
  static async getStudentOverride(studentId, anchorId) {
    try {
      const { data, error } = await supabase
        .from('student_anchor_overrides')
        .select('*')
        .eq('user_id', studentId)
        .eq('anchor_id', anchorId)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 rows gracefully
      
      if (error) {
        console.warn(`Error querying anchor override for ${anchorId}:`, error);
        return null;
      }
      
      return data; // Will be null if no rows found, which is expected
    } catch (error) {
      // No override found - this is normal
      console.warn(`Exception querying anchor override for ${anchorId}:`, error);
      return null;
    }
  }

  /**
   * Set student anchor override
   */
  static async setStudentOverride(studentId, anchorId, overrideDate, reason = null) {
    const { error } = await supabase
      .from('student_anchor_overrides')
      .upsert({
        user_id: studentId,
        anchor_id: anchorId,
        override_date: overrideDate,
        reason: reason
      }, { 
        onConflict: 'user_id,anchor_id' 
      });

    if (error) {
      throw new Error(`Failed to set anchor override: ${error.message}`);
    }

    console.log(`Set anchor override for ${anchorId}:`, overrideDate);
  }

  /**
   * Remove student anchor override
   */
  static async removeStudentOverride(studentId, anchorId) {
    const { error } = await supabase
      .from('student_anchor_overrides')
      .delete()
      .eq('user_id', studentId)
      .eq('anchor_id', anchorId);

    if (error) {
      throw new Error(`Failed to remove anchor override: ${error.message}`);
    }

    console.log(`Removed anchor override for ${anchorId}`);
  }
}

export default AnchorRegistryService;